import { ApolloError, ApolloQueryResult, useQuery } from '@apollo/client';
import { WatchQueryFetchPolicy } from '@apollo/client/core/watchQueryOptions';
import { useEffect, useState } from 'react';

import { isDemoEnvironment } from '../../../constants/global';
import { InitialMockData, KeysMockData } from '../../../DevEnvironment/initialMockData';
import useDevEnvironmentData from '../../../DevEnvironment/useDevEnvironmentData';
import { handleGql403 } from '../../../utils';
import { GetOrganizationQuery, GetOrganizationQueryVariables, Organization } from '../types';
import { GET_ORGANIZATION } from './organization';

export type OrganizationResponse = {
  data?: Organization;
  loading: boolean;
  error?: ApolloError;
  refetch: () => Promise<ApolloQueryResult<GetOrganizationQuery>>;
};

const useFetchOrganization = (
  id?: string,
  fetchPolicy: WatchQueryFetchPolicy = 'network-only'
): OrganizationResponse => {
  const [mockOrganization, setMockOrganization] = useState<Organization>();
  const { getItem } = useDevEnvironmentData();

  const { data, loading, error, refetch } = useQuery<GetOrganizationQuery, GetOrganizationQueryVariables>(
    GET_ORGANIZATION,
    {
      variables: {
        id: String(id),
      },
      skip: !id || !id.length || isDemoEnvironment,
      onError: handleGql403,
      fetchPolicy,
    }
  );

  const refetchMockData = (): Promise<ApolloQueryResult<GetOrganizationQuery>> =>
    getItem('organization' as KeysMockData, InitialMockData.organization.id as never).then((updatedData) => {
      setMockOrganization(updatedData);
    }) as Promise<ApolloQueryResult<GetOrganizationQuery>>;

  useEffect(() => {
    isDemoEnvironment && refetchMockData();
  }, [refetch]);

  return {
    data: isDemoEnvironment ? mockOrganization : data?.organization,
    loading,
    error,
    refetch: isDemoEnvironment ? refetchMockData : refetch,
  };
};

export default useFetchOrganization;
